import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  normalizeCompanyClientStatuses,
  parseCompanyClientStatuses,
} from '@/modules/companies/clientStatus';
import { parseJobState } from '@/modules/jobs/jobStates';
import {
  type JobFilters,
  useJobFilters as useJobFiltersFromStore,
  useJobsActions,
} from '@/modules/jobs/store';
import type { JobsFilterParamsApi } from '@/modules/jobs/types';

export const useJobFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const storedFilters = useJobFiltersFromStore();
  const { setFilters } = useJobsActions();

  useEffect(() => {
    const companyIdParam = searchParams.get('companyId');
    const userIdsParam = searchParams.get('userIds');
    const stateParam = searchParams.get('state');
    const companyClientStatusesParam = searchParams.get(
      'companyClientStatuses',
    );
    const hasUrlParams =
      companyIdParam !== null ||
      userIdsParam !== null ||
      stateParam !== null ||
      companyClientStatusesParam !== null;

    if (hasUrlParams) {
      setFilters({
        companyId: companyIdParam || undefined,
        userIds: userIdsParam
          ? userIdsParam.split(',').map((id) => id.trim())
          : undefined,
        state: parseJobState(stateParam),
        companyClientStatuses: parseCompanyClientStatuses(
          companyClientStatusesParam,
        ),
      });
    } else if (
      storedFilters.companyId ||
      storedFilters.userIds ||
      storedFilters.state ||
      storedFilters.companyClientStatuses
    ) {
      // If no URL params but store has filters, update URL with store values
      const newSearchParams = new URLSearchParams(searchParams);

      if (storedFilters.companyId) {
        newSearchParams.set('companyId', storedFilters.companyId);
      }

      if (storedFilters.userIds && storedFilters.userIds.length > 0) {
        newSearchParams.set('userIds', storedFilters.userIds.join(','));
      }

      const storedState = parseJobState(storedFilters.state ?? null);
      if (storedState) {
        newSearchParams.set('state', storedState);
      }

      const storedCompanyClientStatuses = normalizeCompanyClientStatuses(
        storedFilters.companyClientStatuses ?? [],
      );
      if (storedCompanyClientStatuses) {
        newSearchParams.set(
          'companyClientStatuses',
          storedCompanyClientStatuses.join(','),
        );
      }

      setSearchParams(newSearchParams, { replace: true });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyId = searchParams.get('companyId');
  const userIds = searchParams.get('userIds');
  const state = parseJobState(searchParams.get('state'));
  const companyClientStatuses = parseCompanyClientStatuses(
    searchParams.get('companyClientStatuses'),
  );

  const filters: JobsFilterParamsApi = {
    companyId: companyId || undefined,
    userIds: userIds ? userIds.split(',').map((id) => id.trim()) : undefined,
    state,
    companyClientStatuses,
  };

  const updateFilters = (newFilters: Partial<JobsFilterParamsApi>) => {
    const newSearchParams = new URLSearchParams(searchParams);
    const { state: requestedState, ...filtersWithoutState } = newFilters;
    let filtersToStore: Partial<JobFilters> =
      'state' in newFilters
        ? {
            ...filtersWithoutState,
            state: parseJobState(requestedState ?? null),
          }
        : filtersWithoutState;

    if ('companyId' in newFilters) {
      if (newFilters.companyId) {
        newSearchParams.set('companyId', newFilters.companyId);
      } else {
        newSearchParams.delete('companyId');
      }
    }

    if ('userIds' in newFilters) {
      if (newFilters.userIds && newFilters.userIds.length > 0) {
        newSearchParams.set('userIds', newFilters.userIds.join(','));
      } else {
        newSearchParams.delete('userIds');
      }
    }

    if ('state' in newFilters) {
      const state = parseJobState(newFilters.state ?? null);

      if (state) {
        newSearchParams.set('state', state);
      } else {
        newSearchParams.delete('state');
      }

      filtersToStore = {
        ...filtersToStore,
        state,
      };
    }

    if ('companyClientStatuses' in newFilters) {
      const statuses = normalizeCompanyClientStatuses(
        newFilters.companyClientStatuses ?? [],
      );

      if (statuses) {
        newSearchParams.set('companyClientStatuses', statuses.join(','));
      } else {
        newSearchParams.delete('companyClientStatuses');
      }

      filtersToStore = {
        ...filtersToStore,
        companyClientStatuses: statuses,
      };
    }

    setSearchParams(newSearchParams);
    setFilters(filtersToStore);
  };

  return { filters, updateFilters };
};
